import { get } from 'svelte/store';

import { apiClient, ApiError } from '$lib/api/client';
import { selectedIndustryCentroids } from '$lib/stores/data';
import { queryFilters } from '$lib/stores/selection';
import { listHasGone } from '$lib/api/lists';

/**
 * How large the current selection is, without running it.
 *
 * `POST /deep-iq/count` takes the body `/deep-iq/filter` takes and seeds the
 * same way, so these are the numbers that search returns. That is the whole
 * point of it: the guided search screen shows a running total beside the price
 * it implies, and a total the checkout does not honour is worse than none.
 *
 * The route is not behind Turnstile — a token is single-use, and this is asked
 * on every choice somebody makes. See the note in cmd/ainapi/main.go.
 */
export interface SelectionCount {
  companies: number;
  contacts: number;

  /**
   * Whether a search with these criteria would return these numbers.
   *
   * False when the criteria carry a free-text query: the server applies it
   * structurally and skips the vector pass, which can only take companies
   * away. The guided screen collects no free text, so it always sees true.
   */
  exact: boolean;
}

/**
 * Sequence number of the most recent request.
 *
 * Counts are asked for as fast as somebody can click, and the answers do not
 * have to come back in order. Without this a slow count for "ZH" can land
 * after a fast one for "ZH + BE" and put the wrong number on screen — which is
 * the failure a running total cannot afford, because nothing about it looks
 * wrong.
 */
let latest = 0;

/**
 * Count what the current filters and industry selection would return.
 *
 * Reads the stores rather than taking a request, the way `submitFilters` does,
 * so a caller cannot count one thing and search another.
 *
 * Returns null when the answer is not worth showing: a newer request has
 * overtaken this one, or there are no criteria at all — the server refuses
 * that with a 400 rather than offering the whole database, and on this screen
 * it means "nothing chosen yet", not an error.
 */
export async function fetchCount(): Promise<SelectionCount | null> {
  const sequence = ++latest;

  const filters = get(queryFilters);
  const centroids = Array.from(get(selectedIndustryCentroids).keys()).map(Number);

  try {
    const response = await apiClient.post<SelectionCount>('/deep-iq/count', {
      filters,
      data_pool_ids: centroids,
      contact_mode: filters.contact_mode || 'single'
    });

    if (sequence !== latest) return null;
    return response.data;
  } catch (error) {
    if (sequence !== latest) return null;
    if (error instanceof ApiError && error.status === 400) return null;
    // A count is asked on every click, so it is where an expired customer list
    // is noticed first. The code is let go of here and the count re-asked
    // without it, rather than leaving the running total stuck.
    if (listHasGone(error)) return fetchCount();
    throw error;
  }
}
